import { useHandleSignInCallback, useLogto } from '@logto/react';
import { ossConsolePath } from '@logto/schemas';
import { useEffect } from 'react';
import { type Path, useNavigate } from 'react-router-dom';

import AppLoading from '@/components/AppLoading';
import { isCloud, isMultiTenant } from '@/consts/env';
import { consumeSavedRedirect } from '@/utils/storage';

/**
 * In self-hosted multi-tenant OSS the React Router basename is `/console`, but
 * `saveRedirect()` stores the browser-level pathname (which includes `/console`). Navigating
 * to that saved path without stripping the basename produces `/console/console` and sends
 * the app through a bogus tenant id. Normalize the saved path so `navigate` + basename
 * resolves correctly.
 */
const normalizeSavedPath = (saved: Partial<Path>): Partial<Path> => {
  if (isCloud || !isMultiTenant || !saved.pathname) {
    return saved;
  }
  const normalized = saved.pathname.startsWith(ossConsolePath)
    ? saved.pathname.slice(ossConsolePath.length) || '/'
    : saved.pathname;
  return { ...saved, pathname: normalized };
};

/** The global callback page for all sign-in redirects from Logto main flow. */
function Callback() {
  const navigate = useNavigate();
  const { clearAllTokens } = useLogto();

  useEffect(() => {
    void clearAllTokens();
  }, [clearAllTokens]);

  useHandleSignInCallback(() => {
    const saved = consumeSavedRedirect();

    if (saved) {
      navigate(normalizeSavedPath(saved), { replace: true });
      return;
    }

    navigate('/', { replace: true });
  });

  return <AppLoading />;
}

export default Callback;
